import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BaseRequestService } from '@outlaw/core';
import { catchError, map, Observable, of } from 'rxjs';
import { CommunityItem, LikeResponse } from '../../interfaces';
import { CreateCommunityPost, FileUploadResponse } from '../../interfaces/create-post';
import { LikedUser } from '../../interfaces/liked-user';
import { ShortLikePost } from '../../interfaces/short-like-post';
import { CommunityConfigService } from '../util';

@Injectable({ providedIn: 'root' })
export class PostService {
  #internalApi = inject(CommunityConfigService).internalApi;
  postUrl = this.#internalApi.postUrl;
  filesUrl = this.#internalApi.filesUrl;

  #http = inject(HttpClient);
  #baseRequestService = inject(BaseRequestService);

  createPost(payload: CreateCommunityPost): Observable<CommunityItem> {
    return this.#http.post<CommunityItem>(this.#baseRequestService.prepareUrl([this.postUrl]), payload);
  }

  uploadFile(file: File, relation: string): Observable<FileUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('relation', relation);
    return this.#http.post<FileUploadResponse>(this.#baseRequestService.prepareUrl([this.filesUrl]), formData);
  }

  uploadFiles(files: File[], relation: string): Observable<FileUploadResponse[]> {
    const formData = new FormData();
    files.forEach(file => formData.append('file', file));
    formData.append('relation', relation);
    return this.#http.post<FileUploadResponse[]>(this.#baseRequestService.prepareUrl([this.filesUrl, 'multiple']), formData);
  }

  likeEvent(eventUUID: string): Observable<LikeResponse> {
    return this.#http.post<LikeResponse>(this.#baseRequestService.prepareUrl([this.postUrl, eventUUID, 'like']), {});
  }

  unlikePost(eventUUID: string): Observable<LikeResponse> {
    return this.#http.delete<LikeResponse>(this.#baseRequestService.prepareUrl([this.postUrl, eventUUID, 'like']), {});
  }

  pinPost(postId: string): Observable<void> {
    return this.#http.post<void>(this.#baseRequestService.prepareUrl([this.postUrl, postId, 'pin']), {});
  }

  unpinPost(postId: string): Observable<void> {
    return this.#http.post<void>(this.#baseRequestService.prepareUrl([this.postUrl, postId, 'unpin']), {});
  }

  hideEvent(postId: string): Observable<void> {
    return this.#http.post<void>(this.#baseRequestService.prepareUrl([this.postUrl, postId, 'deactivate']), {});
  }

  getUsersLikedPosts(postUUID: string): Observable<LikedUser[]> {
    return this.#http.get<LikedUser[]>(this.#baseRequestService.prepareUrl([this.postUrl, postUUID, 'likes']));
  }

  userPostsCount(uuid: string): Observable<{ count: number }> {
    return this.#http.get<{ count: number }>(
      this.#baseRequestService.prepareUrl([this.postUrl, 'user_feed', uuid, 'count'])
    );
  }

  getPost(postId: string, options: { skipAuth?: boolean } = {}): Observable<CommunityItem> {
    return this.#http.get<CommunityItem>(this.#baseRequestService.prepareUrl([this.postUrl, postId]), {
      headers: options.skipAuth ? { skip: 'true' } : {},
    });
  }

  getMyFollowingsLikedPosts(postIDs: string[]): Observable<ShortLikePost> {
    let params = new HttpParams({
      fromObject: {
        posts_uuids: postIDs.join(','),
      },
    });

    return this.#http.get<ShortLikePost>(this.#baseRequestService.prepareUrl([this.postUrl, 'liked_by_users_bulk']), {
      params,
    });
  }

  enrichWithLikesInfo(posts: CommunityItem[]): Observable<CommunityItem[]> {
    const postIDs = posts.map(item => item.post_uuid);

    return this.getMyFollowingsLikedPosts(postIDs).pipe(
      catchError(() => of({})),
      map(likes => {
        return posts.map(item => ({
          ...item,
          liked_by: (likes as ShortLikePost)[item.post_uuid] || [],
        }));
      })
    );
  }
}
